import { MMNode, MMRoot, masterModelRoot } from '../model/node-model';

export const DEFAULT_NAMESPACE_PACKAGE = 'default';
export const PACKAGE_NAME_PATTERN = /^[a-z]+(\.[a-z]+)*$/;

export type PlainNamespaceNode = {
  id: string;
  parentId: string | null;
  packageName?: string;
  namespace?: string;
};

const normalizeValue = (value?: string | null): string => {
  return typeof value === 'string' ? value.trim() : '';
};

export const normalizePackageName = (value?: string | null): string => {
  const normalized = normalizeValue(value);
  if (!normalized) return DEFAULT_NAMESPACE_PACKAGE;
  return normalized;
};

export const isPackageNameValid = (value?: string | null): boolean => {
  return PACKAGE_NAME_PATTERN.test(normalizeValue(value));
};

const resolveEffectivePackageName = (value?: string | null): string => {
  const normalized = normalizeValue(value);
  if (!normalized) return DEFAULT_NAMESPACE_PACKAGE;
  if (!PACKAGE_NAME_PATTERN.test(normalized)) return DEFAULT_NAMESPACE_PACKAGE;
  return normalized;
};

export const isTopLevelNode = (node?: MMNode | null): boolean => {
  if (!node) return false;
  return !(node.parent instanceof MMNode);
};

const buildNamespaceFromSegments = (packageName: string, segments: string[]) => {
  return [packageName, ...segments].filter(Boolean).join(':');
};

const applyNamespaceToNode = (node: MMNode, inheritedPackageName?: string) => {
  const pathSegments: string[] = [];
  let cursor: MMNode | MMRoot | null = node;
  while (cursor instanceof MMNode) {
    pathSegments.unshift(String(cursor.id));
    cursor = cursor.parent;
  }

  if (node.parent instanceof MMNode) {
    node.packageName = '';
    node.namespace = buildNamespaceFromSegments(
      resolveEffectivePackageName(inheritedPackageName),
      pathSegments
    );
    return;
  }

  const normalizedPackageName = resolveEffectivePackageName(node.packageName);
  node.packageName = normalizedPackageName;
  node.namespace = buildNamespaceFromSegments(normalizedPackageName, pathSegments);
};

const walkModelNode = (node: MMNode, inheritedPackageName?: string) => {
  applyNamespaceToNode(node, inheritedPackageName);
  const nextPackageName = node.parent instanceof MMNode
    ? inheritedPackageName
    : node.packageName;
  (node.children || []).forEach(child => walkModelNode(child, nextPackageName));
};

export const recalculateNamespacesForMasterModel = (
  root: MMRoot = masterModelRoot
) => {
  (root.children || []).forEach(node => walkModelNode(node));
};

export const recalculateNamespacesForPlainNodes = <T extends PlainNamespaceNode>(
  nodes: T[]
) => {
  const nodeMap = new Map<string, T>();
  const childrenMap = new Map<string | null, T[]>();
  const visited = new Set<string>();

  nodes.forEach(node => {
    nodeMap.set(String(node.id), node);
    const parentId = node.parentId ? String(node.parentId) : null;
    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, []);
    }
    childrenMap.get(parentId)!.push(node);
  });

  const walk = (node: T, inheritedPackageName?: string, treatAsRoot = false) => {
    const currentId = String(node.id);
    if (visited.has(currentId)) return;
    visited.add(currentId);

    if (node.parentId && !treatAsRoot) {
      node.packageName = '';
      node.namespace = [
        resolveEffectivePackageName(inheritedPackageName),
        ...buildPlainPath(node, nodeMap),
      ].join(':');
    } else {
      const normalizedPackageName = resolveEffectivePackageName(node.packageName);
      node.packageName = normalizedPackageName;
      node.namespace = [normalizedPackageName, String(node.id)].join(':');
      inheritedPackageName = normalizedPackageName;
    }

    const children = childrenMap.get(String(node.id)) || [];
    children.forEach(child => walk(child, inheritedPackageName));
  };

  const roots = childrenMap.get(null) || [];
  roots.forEach(rootNode => walk(rootNode));
  nodes.forEach(node => {
    const parentId = node.parentId ? String(node.parentId) : null;
    if (!parentId || nodeMap.has(parentId)) return;
    walk(node, undefined, true);
  });
  return nodes;
};

const buildPlainPath = <T extends PlainNamespaceNode>(
  node: T,
  nodeMap: Map<string, T>
) => {
  const segments: string[] = [String(node.id)];
  let cursorParentId = node.parentId ? String(node.parentId) : null;

  while (cursorParentId) {
    const parent = nodeMap.get(cursorParentId);
    if (!parent) break;
    segments.unshift(String(parent.id));
    cursorParentId = parent.parentId ? String(parent.parentId) : null;
  }

  return segments;
};
